import { useCallback, useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterSection } from '../../components/FilterDropdown';
import type { FilterChip } from '../../components/ActiveFilterChips';
import { fetchBandwidthFilterOptions } from './bandwidthFiltersApi';
import {
  BW_ALL_TASK_TYPES_FILTER_VALUE,
  buildBandwidthAssignableUsersQueryParams,
  fetchBandwidthAssignableUsers,
} from './bandwidthDashboardApi';
import type { BandwidthActivityTypeOption } from './bandwidthDashboardTypes';
import {
  BANDWIDTH_HOUR_STATUS_OPTIONS,
  DEFAULT_BANDWIDTH_HOUR_STATUSES,
  formatBandwidthHourStatusChipValue,
} from './bandwidthHourStatus';
import {
  formatAssignedToUserChipValue,
} from './bandwidthAssigneeFilter';
import { durationPresets, getDurationLabel, type DurationPreset } from '../dashboard/utils/dateHelpers';

const BW_DURATION_OPTIONS = [
  { value: '', label: 'All Time' },
  ...durationPresets
    .filter((p) => p.value !== 'custom')
    .map((p) => ({ value: `preset:${p.value}`, label: p.label })),
];

const defaultState = {
  /** Default: this calendar month through today. Empty string = all time (earliest data in DB). */
  duration: 'preset:this_month' as string,
  customerId: 'All',
  timeEntryTypes: [BW_ALL_TASK_TYPES_FILTER_VALUE] as string[],
  timeEntryStatus: [...DEFAULT_BANDWIDTH_HOUR_STATUSES],
  assignmentGroupId: 'All',
  assignedToMe: '' as string,
  assignedToUserId: [] as string[],
};

function durationChipLabel(durationValue: string): string {
  if (!durationValue) return 'All Time';
  if (durationValue.startsWith('custom:')) {
    const [, dates] = durationValue.split('custom:');
    const [from, to] = (dates || '').split(',');
    return getDurationLabel('custom', from, to);
  }
  if (durationValue.startsWith('preset:')) {
    const preset = durationValue.replace('preset:', '') as DurationPreset;
    return getDurationLabel(preset);
  }
  return durationValue;
}

function userLabel(u: { id: string; firstName?: string; lastName?: string }): string {
  const n = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return n || u.id;
}

export type BandwidthFilterBarMode = 'tasksCustomers' | 'countTime' | 'dates' | 'users';

/**
 * @param filterBarMode Which filters appear in the bar; also drives assignable-users query scope.
 */
export function useBandwidthFilters(filterBarMode: BandwidthFilterBarMode = 'tasksCustomers') {
  const [durationFilter, setDurationFilter] = useState(defaultState.duration);
  const [customerId, setCustomerId] = useState(defaultState.customerId);
  const [timeEntryTypes, setTimeEntryTypes] = useState<string[]>(defaultState.timeEntryTypes);
  const [timeEntryStatus, setTimeEntryStatus] = useState<string[]>(defaultState.timeEntryStatus);
  const [assignmentGroupId, setAssignmentGroupId] = useState(defaultState.assignmentGroupId);
  const [assignedToMe, setAssignedToMe] = useState(defaultState.assignedToMe);
  const [assignedToUserIds, setAssignedToUserIds] = useState<string[]>(defaultState.assignedToUserId);
  const [availableActivityTypes, setAvailableActivityTypes] = useState<BandwidthActivityTypeOption[]>([]);

  const filtersQuery = useQuery({
    queryKey: ['bandwidth-filter-options'],
    queryFn: fetchBandwidthFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  const scopeUsesAssignmentGroup =
    filterBarMode === 'countTime' || filterBarMode === 'tasksCustomers';

  const assignableScopeKey = useMemo(
    () =>
      [
        durationFilter,
        customerId,
        timeEntryTypes.slice().sort().join(','),
        timeEntryStatus.slice().sort().join(','),
        scopeUsesAssignmentGroup ? assignmentGroupId : '',
      ].join('|'),
    [durationFilter, customerId, timeEntryTypes, timeEntryStatus, assignmentGroupId, scopeUsesAssignmentGroup]
  );

  const assignableQueryParams = useMemo(
    () =>
      buildBandwidthAssignableUsersQueryParams(
        {
          duration: durationFilter,
          customer: customerId,
          timeEntryType: timeEntryTypes,
          timeEntryStatus,
          assignmentGroup: assignmentGroupId,
          assignedToMe: '',
          assignedToUserId: [],
        },
        { includeAssignmentGroup: scopeUsesAssignmentGroup }
      ),
    [
      durationFilter,
      customerId,
      timeEntryTypes,
      timeEntryStatus,
      assignmentGroupId,
      scopeUsesAssignmentGroup,
    ]
  );

  const usersQuery = useQuery({
    queryKey: ['bandwidth-assignable-users', assignableScopeKey],
    queryFn: () => fetchBandwidthAssignableUsers(assignableQueryParams),
    staleTime: 60_000,
    enabled:
      filterBarMode === 'countTime' ||
      filterBarMode === 'users' ||
      filterBarMode === 'tasksCustomers' ||
      filterBarMode === 'dates',
  });

  const filterKey = useMemo(
    () =>
      [
        durationFilter,
        customerId,
        timeEntryTypes.slice().sort().join(','),
        timeEntryStatus.slice().sort().join(','),
        assignmentGroupId,
        assignedToMe,
        assignedToUserIds.slice().sort().join(','),
      ].join('-'),
    [durationFilter, customerId, timeEntryTypes, timeEntryStatus, assignmentGroupId, assignedToMe, assignedToUserIds]
  );

  const filterValues = useMemo(
    () => ({
      duration: durationFilter,
      customer: customerId,
      timeEntryType: timeEntryTypes,
      timeEntryStatus,
      assignmentGroup: assignmentGroupId,
      assignedToMe,
      assignedToUserId: assignedToUserIds,
    }),
    [durationFilter, customerId, timeEntryTypes, timeEntryStatus, assignmentGroupId, assignedToMe, assignedToUserIds]
  );

  const dashboardFilters = filtersQuery.data;
  const customers = useMemo(
    () => dashboardFilters?.customers ?? [],
    [dashboardFilters?.customers]
  );
  const assignmentGroups = useMemo(
    () => dashboardFilters?.assignmentGroups ?? [],
    [dashboardFilters?.assignmentGroups]
  );
  const assignableUsers = usersQuery.data ?? [];

  useEffect(() => {
    if (usersQuery.isLoading || usersQuery.isFetching) return;
    setAssignedToUserIds((prev) => {
      const next = prev.filter((id) => assignableUsers.some((u) => u.id === id));
      return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
    });
  }, [usersQuery.isLoading, usersQuery.isFetching, assignableUsers]);

  const assignedToUserChipValue = useCallback(
    (ids: string[]) =>
      formatAssignedToUserChipValue(ids, (id) =>
        userLabel(assignableUsers.find((u) => u.id === id) ?? { id })
      ),
    [assignableUsers]
  );

  const filterSections: FilterSection[] = useMemo(() => {
    const durationSection: FilterSection = {
      id: 'duration',
      label: 'Duration',
      type: 'time-range',
      options: BW_DURATION_OPTIONS,
      placeholder: 'All Time',
      timeRangeCoerceToDayBounds: true,
      timeRangeDefaultFromTime: '00:00',
      timeRangeDefaultToTime: '23:59',
    };
    const customerSection: FilterSection = {
      id: 'customer',
      label: 'Customer',
      type: 'searchable-select',
      options: [{ value: 'All', label: 'All' }, ...customers.map((c) => ({ value: c.id, label: c.accountName }))],
    };
    const taskTypesSection: FilterSection = {
      id: 'timeEntryType',
      label: 'Task Types',
      type: 'searchable-multi-select',
      options:
        availableActivityTypes.length === 0
          ? []
          : [
              { value: BW_ALL_TASK_TYPES_FILTER_VALUE, label: 'All Task Types' },
              ...availableActivityTypes.map((t) => ({ value: t.value, label: t.label })),
            ],
      allOptionValue:
        availableActivityTypes.length > 0 ? BW_ALL_TASK_TYPES_FILTER_VALUE : undefined,
    };
    const hourStatusSection: FilterSection = {
      id: 'timeEntryStatus',
      label: 'Hour Status',
      type: 'searchable-multi-select',
      options: BANDWIDTH_HOUR_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    };

    const assignedToUserMultiOptions =
      usersQuery.isLoading
        ? []
        : assignableUsers.map((u) => ({ value: u.id, label: userLabel(u) }));

    const teamsSection: FilterSection = {
      id: 'assignmentGroup',
      label: 'Teams',
      type: 'searchable-select',
      options: [
        { value: 'All', label: 'All' },
        ...assignmentGroups.map((g) => ({ value: g.id, label: g.name })),
      ],
      placeholder: 'All',
    };

    const assignedToMeSection: FilterSection = {
      id: 'assignedToMe',
      label: 'Assigned To Me',
      type: 'single-select',
      options: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Yes' },
      ],
      placeholder: 'All',
    };
    const assignedToUserSection: FilterSection = {
      id: 'assignedToUserId',
      label: 'Assigned To',
      valuePanelLabel: `Assigned To (${assignableUsers.length})`,
      type: 'searchable-multi-select',
      options: assignedToUserMultiOptions,
      placeholder: 'All Users',
      suppressEmptySelectWhenAssignedToMe: true,
    };

    if (filterBarMode === 'tasksCustomers') {
      return [
        durationSection,
        customerSection,
        teamsSection,
        assignedToMeSection,
        assignedToUserSection,
        hourStatusSection,
        taskTypesSection,
      ];
    }

    if (filterBarMode === 'dates') {
      return [
        durationSection,
        customerSection,
        assignedToMeSection,
        assignedToUserSection,
        hourStatusSection,
        taskTypesSection,
      ];
    }

    if (filterBarMode === 'users') {
      return [
        durationSection,
        taskTypesSection,
        assignedToMeSection,
        assignedToUserSection,
        customerSection,
        hourStatusSection,
      ];
    }

    return [
      durationSection,
      {
        id: 'assignmentGroup',
        label: 'Assignment Group',
        type: 'searchable-select',
        options: [
          { value: 'All', label: 'All' },
          ...assignmentGroups.map((g) => ({ value: g.id, label: g.name })),
        ],
        placeholder: 'All',
      },
      assignedToMeSection,
      assignedToUserSection,
      customerSection,
      taskTypesSection,
      hourStatusSection,
    ];
  }, [customers, availableActivityTypes, filterBarMode, assignmentGroups, assignableUsers, usersQuery.isLoading]);

  const assigneeFilterChips: FilterChip[] = useMemo(() => {
    const meChip =
      assignedToMe === 'true'
        ? ({
            id: 'assignedToMe',
            label: 'Assigned To Me',
            value: 'Yes',
            color: 'purple' as const,
          } satisfies FilterChip)
        : null;
    const userChip =
      assignedToUserIds.length > 0 && assignedToMe !== 'true'
        ? ({
            id: 'assignedToUserId',
            label: 'Assigned To',
            value: assignedToUserChipValue(assignedToUserIds),
            color: 'blue' as const,
          } satisfies FilterChip)
        : null;
    return [meChip, userChip].filter(Boolean) as FilterChip[];
  }, [assignedToMe, assignedToUserIds, assignedToUserChipValue]);

  const countTimeChips: FilterChip[] = useMemo(() => {
    if (filterBarMode !== 'countTime') return [];
    const groupLabel =
      assignmentGroupId !== 'All'
        ? assignmentGroups.find((g) => g.id === assignmentGroupId)?.name ?? assignmentGroupId
        : null;
    const groupChip =
      groupLabel != null
        ? ({ id: 'assignmentGroup', label: 'Assignment Group', value: groupLabel, color: 'gray' as const } satisfies FilterChip)
        : null;
    return [groupChip, ...assigneeFilterChips].filter(Boolean) as FilterChip[];
  }, [filterBarMode, assignmentGroupId, assignmentGroups, assigneeFilterChips]);

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const concreteTypes = timeEntryTypes.filter((t) => t !== BW_ALL_TASK_TYPES_FILTER_VALUE);
    const isAllTaskTypes =
      timeEntryTypes.length === 0 ||
      (timeEntryTypes.length === 1 && timeEntryTypes[0] === BW_ALL_TASK_TYPES_FILTER_VALUE);

    const isDefaultHourStatuses =
      timeEntryStatus.slice().sort().join('|') === DEFAULT_BANDWIDTH_HOUR_STATUSES.slice().sort().join('|');

    const chips: FilterChip[] = [];

    if (durationFilter.trim() !== '') {
      chips.push({ id: 'duration', label: 'Duration', value: durationChipLabel(durationFilter), color: 'green' });
    }

    if (filterBarMode === 'tasksCustomers' && assignmentGroupId !== 'All') {
      const groupLabel =
        assignmentGroups.find((g) => g.id === assignmentGroupId)?.name ?? assignmentGroupId;
      chips.push({ id: 'assignmentGroup', label: 'Teams', value: groupLabel, color: 'orange' as const });
    }

    if (
      filterBarMode === 'countTime' ||
      filterBarMode === 'users' ||
      filterBarMode === 'tasksCustomers' ||
      filterBarMode === 'dates'
    ) {
      if (filterBarMode === 'countTime') {
        chips.push(...countTimeChips);
      } else {
        chips.push(...assigneeFilterChips);
      }
    }

    if (customerId && customerId !== 'All') {
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customers.find((c) => c.id === customerId)?.accountName || customerId,
        color: 'teal',
      });
    }

    if (!isDefaultHourStatuses) {
      chips.push({
        id: 'timeEntryStatus',
        label: 'Hour Status',
        value: formatBandwidthHourStatusChipValue(timeEntryStatus),
        color: 'cyan',
      });
    }

    if (!isAllTaskTypes) {
      chips.push({
        id: 'timeEntryType',
        label: 'Task Types',
        value: concreteTypes.map((v) => availableActivityTypes.find((t) => t.value === v)?.label ?? v).join(', '),
        color: 'gray',
      });
    }

    return chips;
  }, [
    durationFilter,
    customerId,
    timeEntryTypes,
    timeEntryStatus,
    customers,
    availableActivityTypes,
    filterBarMode,
    countTimeChips,
    assigneeFilterChips,
    assignmentGroupId,
    assignmentGroups,
  ]);

  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'duration':
        setDurationFilter(typeof value === 'string' ? value : '');
        break;
      case 'customer':
        setCustomerId((value as string) || 'All');
        break;
      case 'timeEntryType': {
        let next = Array.isArray(value) ? [...value] : [];
        if (next.includes(BW_ALL_TASK_TYPES_FILTER_VALUE) && next.length > 1) {
          next = next.filter((v) => v !== BW_ALL_TASK_TYPES_FILTER_VALUE);
        }
        if (next.length === 0) next = [BW_ALL_TASK_TYPES_FILTER_VALUE];
        setTimeEntryTypes(next);
        break;
      }
      case 'timeEntryStatus': {
        const next = Array.isArray(value) ? value : [];
        setTimeEntryStatus(next.length > 0 ? next : [...DEFAULT_BANDWIDTH_HOUR_STATUSES]);
        break;
      }
      case 'assignmentGroup':
        setAssignmentGroupId((value as string) || 'All');
        break;
      case 'assignedToMe': {
        const v = (value as string) || '';
        setAssignedToMe(v);
        if (v === 'true') setAssignedToUserIds([]);
        break;
      }
      case 'assignedToUserId': {
        const next = Array.isArray(value) ? value.map((id) => id.trim()).filter(Boolean) : [];
        setAssignedToUserIds(next);
        if (next.length > 0) setAssignedToMe('');
        break;
      }
      default:
        break;
    }
  };

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'duration':
        setDurationFilter('');
        break;
      case 'customer':
        setCustomerId('All');
        break;
      case 'timeEntryType':
        setTimeEntryTypes([BW_ALL_TASK_TYPES_FILTER_VALUE]);
        break;
      case 'timeEntryStatus':
        setTimeEntryStatus([...DEFAULT_BANDWIDTH_HOUR_STATUSES]);
        break;
      case 'assignmentGroup':
        setAssignmentGroupId('All');
        break;
      case 'assignedToMe':
        setAssignedToMe('');
        break;
      case 'assignedToUserId':
        setAssignedToUserIds([]);
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setDurationFilter(defaultState.duration);
    setCustomerId(defaultState.customerId);
    setTimeEntryTypes(defaultState.timeEntryTypes);
    setTimeEntryStatus([...DEFAULT_BANDWIDTH_HOUR_STATUSES]);
    setAssignmentGroupId(defaultState.assignmentGroupId);
    setAssignedToMe(defaultState.assignedToMe);
    setAssignedToUserIds(defaultState.assignedToUserId);
  };

  return {
    filterKey,
    filterValues,
    filterSections,
    activeFilterChips,
    customers,
    assignmentGroups,
    setTimeEntryTypes,
    setAvailableActivityTypes,
    handleFilterChange,
    handleRemoveFilterChip,
    handleClearAllFilters,
  };
}
